// src/pages/subscription/PaymentSuccessPage.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Package, Calendar, CreditCard, ArrowRight, Loader } from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAuth } from '../../hooks/useAuth';
import { getMySubscription } from '../../services/api/subscription';
import { formatPrice } from '../../utils/subscriptionUtils';
import { formatDate } from '../../utils/dateUtils';
import styles from './PaymentSuccessPage.module.css';

const PaymentSuccessPage = () => {
  const { t } = useLocalization();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);

  const orderId = searchParams.get('order_id');
  const currency = user?.business?.currency || 'LKR';

  useEffect(() => {
    loadSubscriptionData();
    
    // Trigger confetti animation
    setTimeout(() => setShowConfetti(true), 300);
  }, []);

  const loadSubscriptionData = async () => {
    try {
      setIsLoading(true);
      const response = await getMySubscription();
      setSubscription(response.subscription);
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleViewSubscription = () => {
    navigate('/subscription');
  };

  return (
    <div className={styles.paymentSuccessPage}>
      {showConfetti && <div className={styles.confetti} />}
      
      <div className={styles.successCard}>
        <div className={styles.iconWrapper}>
          <div className={styles.successIcon}>
            <CheckCircle size={64} />
          </div>
        </div>

        <div className={styles.successHeader}>
          <h1>{t('subscription.payment.successTitle')}</h1>
          <p>{t('subscription.payment.successMessage')}</p>
        </div>

        {orderId && (
          <div className={styles.orderInfo}>
            <div className={styles.orderLabel}>{t('subscription.payment.orderId')}</div>
            <div className={styles.orderValue}>{orderId}</div>
          </div>
        )}

        {isLoading ? (
          <div className={styles.loadingState}>
            <Loader className={styles.spinner} size={32} />
            <p>{t('common.loading')}</p>
          </div>
        ) : subscription ? (
          <div className={styles.subscriptionDetails}>
            <h2>{t('subscription.payment.subscriptionDetails')}</h2>
            
            <div className={styles.detailsGrid}>
              <div className={styles.detailItem}>
                <div className={styles.detailIcon}>
                  <Package size={20} />
                </div>
                <div className={styles.detailContent}>
                  <div className={styles.detailLabel}>{t('subscription.planName')}</div>
                  <div className={styles.detailValue}>{subscription.plan?.name}</div>
                </div>
              </div>

              <div className={styles.detailItem}>
                <div className={styles.detailIcon}>
                  <CreditCard size={20} />
                </div>
                <div className={styles.detailContent}>
                  <div className={styles.detailLabel}>{t('subscription.amount')}</div>
                  <div className={styles.detailValue}>
                    {formatPrice(subscription.plan?.price || 0, currency)}
                  </div>
                </div>
              </div>

              <div className={styles.detailItem}>
                <div className={styles.detailIcon}>
                  <Calendar size={20} />
                </div>
                <div className={styles.detailContent}>
                  <div className={styles.detailLabel}>{t('subscription.billingCycle')}</div>
                  <div className={styles.detailValue}>
                    {t(`subscription.${subscription.billingCycle}`)}
                  </div>
                </div>
              </div>

              {subscription.nextBillingDate && (
                <div className={styles.detailItem}>
                  <div className={styles.detailIcon}>
                    <Calendar size={20} />
                  </div>
                  <div className={styles.detailContent}>
                    <div className={styles.detailLabel}>{t('subscription.nextBillingDate')}</div>
                    <div className={styles.detailValue}>
                      {formatDate(subscription.nextBillingDate)}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.featuresList}>
              <h3>{t('subscription.payment.accessGranted')}</h3>
              <ul>
                {subscription.plan?.features?.map((feature, index) => (
                  <li key={index}>
                    <CheckCircle size={16} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div className={styles.noSubscription}>
            <p>{t('subscription.payment.noSubscriptionFound')}</p>
          </div>
        )}

        <div className={styles.actionButtons}>
          <button 
            className={styles.primaryButton}
            onClick={handleGoToDashboard}
          >
            {t('subscription.payment.goToDashboard')}
            <ArrowRight size={18} />
          </button>
          <button 
            className={styles.secondaryButton}
            onClick={handleViewSubscription}
          >
            {t('subscription.payment.viewSubscription')}
          </button>
        </div>

        <div className={styles.footer}>
          <p>{t('subscription.payment.confirmationEmail')}</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;